/**
 * The line's health for its admins (docs/plans/oncall-line-plan.md 4, 7.2,
 * 7.5 GET /health, 7.7; WP-11): the switches as the api reads them, the
 * Twilio account, the last heartbeat and the active members. Every value is
 * a boolean or a count (routes/oncall/health.js switchesSnapshot): the page
 * never sees a number, a key, a URL or a SID. Each row pairs its state word
 * with a colour, never the colour alone.
 *
 * A heartbeat older than HEARTBEAT_STALE_HOURS (or none at all while the
 * heartbeat is configured) is flagged: the daily call from
 * ONCALL_HEARTBEAT_FROM tests the number, its webhook, the api and the data
 * plane end to end (plan 4 layer 4).
 */
import type { ReactNode } from 'react';
import { Activity, AlertTriangle, CheckCircle2, CircleSlash, Info } from 'lucide-react';
import { secondaryBtnCls } from '../../../ui/styles';
import type { OncallHealth } from '../../api/oncallApi';
import { formatDateTime } from '../../lib/format';

/** The heartbeat runs daily; a day and two hours without one is a failure to look at. */
export const HEARTBEAT_STALE_HOURS = 26;

type Tone = 'ok' | 'warn' | 'bad' | 'off';

const TONE_PILL: Record<Tone, string> = {
  ok: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  bad: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  off: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

const TONE_ICON: Record<Tone, typeof CheckCircle2> = {
  ok: CheckCircle2,
  warn: AlertTriangle,
  bad: AlertTriangle,
  off: CircleSlash,
};

interface Row {
  key: string;
  label: string;
  tone: Tone;
  state: string;
  hint?: ReactNode;
}

export function healthRows(health: OncallHealth, timeZone: string | null | undefined, now: Date = new Date()): Row[] {
  const sw = health.switches;
  const rows: Row[] = [];

  rows.push({
    key: 'enabled',
    label: 'Line switched on (ONCALL_ENABLED)',
    tone: sw.enabled ? 'ok' : 'warn',
    state: sw.enabled ? 'On' : 'Off',
    hint: sw.enabled
      ? 'Calls to the number run the hunt.'
      : 'Every call is answered by the Twilio fallback Bin, which rings the backup phones.',
  });
  rows.push({
    key: 'twilio',
    label: 'Twilio account',
    tone: health.twilio_configured ? 'ok' : 'bad',
    state: health.twilio_configured ? 'Set' : 'Missing',
  });
  rows.push({
    key: 'encryption',
    label: 'Encryption key (ONCALL_ENCRYPTION_KEY)',
    tone: sw.encryption_key_configured ? 'ok' : 'bad',
    state: sw.encryption_key_configured ? 'Set' : 'Missing',
    hint: sw.encryption_key_configured ? undefined : 'Calls still connect, but no transcript or caller number is saved.',
  });
  rows.push({
    key: 'voicemail',
    label: 'Voicemail storage (GCS_ONCALL_BUCKET)',
    tone: sw.voicemail_storage_configured ? 'ok' : 'warn',
    state: sw.voicemail_storage_configured ? 'Set' : 'Not set',
    hint: sw.voicemail_storage_configured ? undefined : 'No voicemail: callers are asked to call back.',
  });
  rows.push({
    key: 'public-url',
    label: 'Public API address (ONCALL_PUBLIC_API_URL)',
    tone: sw.public_api_url_configured ? 'ok' : 'warn',
    state: sw.public_api_url_configured ? 'Set' : 'Not set',
    hint: sw.public_api_url_configured ? undefined : 'Test calls and SiteOps callbacks are refused.',
  });
  rows.push({
    key: 'fallback-numbers',
    label: 'Last-resort numbers (ONCALL_FALLBACK_NUMBERS)',
    tone: sw.fallback_numbers_count > 0 ? 'ok' : 'warn',
    state: String(sw.fallback_numbers_count),
    hint: 'Rung when SiteOps cannot read this line or nobody is active.',
  });

  const sms = sw.alert_sms_enabled && sw.alert_messaging_service_configured;
  rows.push({
    key: 'sms',
    label: 'Missed-call texts',
    tone: sms ? 'ok' : 'off',
    state: sms ? 'On' : 'Email only',
    hint: sms
      ? 'Every active member is texted about a missed call.'
      : 'Texts wait on A2P approval (ONCALL_ALERT_SMS_ENABLED and ONCALL_ALERT_MESSAGING_SERVICE_SID).',
  });

  // No line yet: nothing to heartbeat and nobody to count, so neither row shows.
  if (health.line && !sw.heartbeat_configured) {
    rows.push({
      key: 'heartbeat',
      label: 'Daily heartbeat call',
      tone: 'off',
      state: 'Off',
      hint: 'ONCALL_HEARTBEAT_FROM is not set.',
    });
  } else if (health.line) {
    const last = health.last_heartbeat_at ? Date.parse(health.last_heartbeat_at) : NaN;
    const fresh = Number.isFinite(last) && now.getTime() - last <= HEARTBEAT_STALE_HOURS * 3600 * 1000;
    rows.push({
      key: 'heartbeat',
      label: 'Daily heartbeat call',
      tone: fresh ? 'ok' : 'bad',
      state: Number.isFinite(last) ? formatDateTime(health.last_heartbeat_at, timeZone, { withZone: true }) : 'Never',
      hint: fresh ? 'The last test call reached the line.' : 'No heartbeat reached this line in the last day. Check the number and the api.',
    });
  }

  if (health.line) {
    rows.push({
      key: 'members',
      label: 'Active members',
      tone: health.active_members >= 3 ? 'ok' : health.active_members > 0 ? 'warn' : 'bad',
      state: String(health.active_members),
      hint: health.active_members >= 3 ? undefined : 'Keep at least 3 so a call always reaches someone.',
    });
  }

  if (sw.dial_override_active) {
    rows.push({
      key: 'override',
      label: 'Test mode (ONCALL_DIAL_OVERRIDE_TO)',
      tone: 'warn',
      state: 'On',
      hint: 'Every call the line places rings one test number instead of the members.',
    });
  }
  return rows;
}

export interface HealthPanelProps {
  health: OncallHealth | null;
  error: string | null;
  timeZone: string | null | undefined;
  onRetry: () => void;
  /** "Now" for the heartbeat age (tests pass a fixed instant). */
  now?: Date;
}

export default function HealthPanel({ health, error, timeZone, onRetry, now }: HealthPanelProps) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800" aria-labelledby="oncall-health-title" data-testid="oncall-health">
      <h2 id="oncall-health-title" className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        <Activity className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        Health
      </h2>
      {error ? (
        <div role="alert" className="mt-3 flex flex-wrap items-center gap-2 text-sm text-red-700 dark:text-red-400">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">{error}</span>
          <button type="button" onClick={onRetry} className={secondaryBtnCls}>
            Retry
          </button>
        </div>
      ) : !health ? (
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : (
        <dl className="mt-3 divide-y divide-gray-100 dark:divide-gray-700">
          {healthRows(health, timeZone, now).map((row) => {
            const Icon = TONE_ICON[row.tone];
            return (
              <div key={row.key} className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 py-2" data-testid={`oncall-health-${row.key}`}>
                <dt className="min-w-0 flex-1 text-sm text-gray-700 dark:text-gray-200">
                  {row.label}
                  {row.hint && <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{row.hint}</span>}
                </dt>
                <dd>
                  <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium tabular-nums ${TONE_PILL[row.tone]}`}>
                    <Icon className="h-3 w-3" aria-hidden="true" />
                    {row.state}
                  </span>
                </dd>
              </div>
            );
          })}
        </dl>
      )}
      <p className="mt-3 flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
        <Info className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
        Switches are set on the api by the launch runbook; this page only reads them.
      </p>
    </section>
  );
}
